import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { fetchProducts } from '../api';
import { Loader2, Instagram, Music2, ShoppingBag, Tag } from 'lucide-react';
import { NewsletterSignup } from '../components/NewsletterSignup';

export const Home = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await fetchProducts();
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const scrollToShop = () => {
    document.getElementById('jackets')?.scrollIntoView({ behavior: 'smooth' });
  };

  const getProductsByCategory = (category) => {
    return products.filter(p => p.category === category);
  };

  const renderSection = (id, title, category, darkBg = false) => {
    const sectionProducts = getProductsByCategory(category);
    return (
      <section
        id={id}
        className={`py-28 px-[8%] ${darkBg ? 'bg-black/30' : ''}`}
        data-testid={`section-${category}`}
      >
        <h2 className="text-center text-5xl font-extrabold text-[#ff1f4d] uppercase mb-16 tracking-wide">
          {title}
        </h2>
        {sectionProducts.length === 0 ? (
          <p className="text-center text-gray-500 italic">No products available yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sectionProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>
    );
  };

  // Marquee tagline strip — running banner
  const TaglineBanner = () => (
    <div className="bg-[#ff1f4d] py-4 overflow-hidden border-y-2 border-black" data-testid="tagline-banner">
      <div className="flex whitespace-nowrap animate-marquee">
        {Array(8).fill(0).map((_, i) => (
          <span key={i} className="text-black font-black text-2xl tracking-[6px] mx-12 uppercase">
            Faith • Fashion • Streetwear ✦ Stay Turnt ✦
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="h-screen flex items-center justify-center text-center px-5 relative"
        style={{
          background: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.85)), url('https://images.unsplash.com/photo-1523398002811-999ca8dec234?q=80&w=1974&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="animate-fadeIn">
          <p className="text-[#ff1f4d] text-sm tracking-[8px] mb-4 uppercase font-bold">
            2.TURNT Industries
          </p>
          <h1
            className="text-7xl md:text-[95px] font-black text-[#ff1f4d] tracking-[5px] mb-5"
            style={{ textShadow: '0 0 25px rgba(255,31,77,0.8)' }}
            data-testid="hero-title"
          >
            STAY TURNT
          </h1>
          <p className="text-xl tracking-[3px] text-gray-300 mb-9">
            Faith • Fashion • Streetwear
          </p>
          <Button
            onClick={scrollToShop}
            data-testid="shop-now-btn"
            className="bg-[#ff1f4d] hover:bg-white hover:text-black font-bold text-base px-11 py-7 transition-all duration-300 hover:-translate-y-1"
          >
            SHOP THE DROP
          </Button>
        </div>
      </section>

      {/* Tagline banner immediately after hero */}
      <TaglineBanner />

      {/* Promo code teaser strip */}
      <div className="bg-black border-y border-[#ff1f4d]/30 py-4 px-5 text-center" data-testid="promo-strip">
        <p className="text-white text-sm md:text-base tracking-wide">
          <Tag className="inline h-4 w-4 text-[#ff1f4d] mr-2 -mt-0.5" />
          Use code <span className="text-[#ff1f4d] font-black tracking-widest mx-1">STAYTURNT10</span>
          for 10% off your first order
          <span className="hidden md:inline text-gray-500 ml-3">•</span>
          <span className="hidden md:inline text-gray-400 ml-3">Free shipping on orders over $100</span>
        </p>
      </div>

      {loading ? (
        <div className="py-28 flex justify-center items-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#ff1f4d]" />
        </div>
      ) : (
        <>
          {renderSection('jackets', 'Jackets', 'jackets')}
          {renderSection('pants', 'Pants & Denim', 'pants', true)}
          {renderSection('shorts', 'Shorts', 'shorts')}
          {renderSection('accessories', 'Accessories', 'accessories', true)}
        </>
      )}

      {/* Second marquee strip before about */}
      <TaglineBanner />

      {/* About Section — Manifesto Style */}
      <section className="py-32 px-[8%] relative overflow-hidden" data-testid="about-section">
        {/* Giant background watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.03]">
          <span className="text-[20rem] font-black text-[#ff1f4d] tracking-tighter">2T</span>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto">
          <p className="text-center text-[#ff1f4d] text-sm tracking-[8px] mb-4 uppercase font-bold">
            The Movement
          </p>
          <h2 className="text-center text-6xl md:text-7xl font-black text-white uppercase mb-12 tracking-tight leading-none">
            More than <span className="text-[#ff1f4d]">clothing.</span>
            <br />
            This is a <span className="text-[#ff1f4d]">lifestyle.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mt-20">
            <div className="text-center p-8 border border-[#222] rounded-lg bg-[#0a0a0a] hover:border-[#ff1f4d] transition-colors duration-300">
              <div className="text-[#ff1f4d] text-5xl font-black mb-4">01</div>
              <h3 className="text-white text-xl font-bold mb-3 uppercase tracking-wide">Faith</h3>
              <p className="text-gray-400 leading-relaxed">
                Designs rooted in conviction. Every piece carries meaning — religious art, divine
                imagery, and statements of belief sewn into the fabric.
              </p>
            </div>

            <div className="text-center p-8 border border-[#222] rounded-lg bg-[#0a0a0a] hover:border-[#ff1f4d] transition-colors duration-300">
              <div className="text-[#ff1f4d] text-5xl font-black mb-4">02</div>
              <h3 className="text-white text-xl font-bold mb-3 uppercase tracking-wide">Fashion</h3>
              <p className="text-gray-400 leading-relaxed">
                Premium fabrics. Bold graphics. Limited drops. Designed for those who refuse to
                blend in — every stitch built to stand out.
              </p>
            </div>

            <div className="text-center p-8 border border-[#222] rounded-lg bg-[#0a0a0a] hover:border-[#ff1f4d] transition-colors duration-300">
              <div className="text-[#ff1f4d] text-5xl font-black mb-4">03</div>
              <h3 className="text-white text-xl font-bold mb-3 uppercase tracking-wide">Streetwear</h3>
              <p className="text-gray-400 leading-relaxed">
                Born on the streets, made for the bold. 2.TURNT moves with confidence —
                authentic, unapologetic, and built different.
              </p>
            </div>
          </div>

          <p className="text-center text-gray-300 text-xl leading-relaxed mt-20 max-w-3xl mx-auto italic">
            "Don't try. <span className="text-[#ff1f4d] font-bold">Stay Turnt.</span>"
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#050505] py-16 px-5 border-t border-[#222]" data-testid="footer">
        <div className="max-w-3xl mx-auto mb-12">
          <NewsletterSignup />
        </div>

        <div className="text-center">
          <h2 className="text-[#ff1f4d] text-4xl font-black mb-2 tracking-widest">2.TURNT</h2>
          <p className="text-gray-500 text-sm tracking-[4px] uppercase mb-8">Industries</p>

          <div className="flex justify-center gap-8 mb-8">
            <a href="#" className="text-white hover:text-[#ff1f4d] transition-colors flex items-center gap-2" data-testid="footer-instagram">
              <Instagram className="h-5 w-5" />
              <span className="uppercase text-sm tracking-wider font-bold">Instagram</span>
            </a>
            <a href="#" className="text-white hover:text-[#ff1f4d] transition-colors flex items-center gap-2" data-testid="footer-tiktok">
              <Music2 className="h-5 w-5" />
              <span className="uppercase text-sm tracking-wider font-bold">TikTok</span>
            </a>
            <a href="#" className="text-white hover:text-[#ff1f4d] transition-colors flex items-center gap-2" data-testid="footer-shop">
              <ShoppingBag className="h-5 w-5" />
              <span className="uppercase text-sm tracking-wider font-bold">Shop</span>
            </a>
          </div>

          <p className="text-gray-600 text-xs tracking-[3px] uppercase mb-4">
            Faith • Fashion • Streetwear
          </p>

          <p className="text-gray-700 text-xs">
            © 2026 2.TURNT INDUSTRIES — All Rights Reserved
          </p>
        </div>
      </footer>
    </div>
  );
};
